import { useContext } from 'react';
import { Module } from '../api/module';
import IdentityContext from '../contexts/IdentityContext';
import AccountContext from '../contexts/AccountContext';
import useStudioGroup from './useStudioGroup';
import { getModuleLicense, isLicensed, MODULE_LICENSE } from '../core/access/module-access';

export const LICENSE_VALIDATION_TYPES = {
  LICENSED: 'LICENSED',
  STUDENT_NO_DOCUMENT: 'STUDENT_NO_DOCUMENT',
  TEACHER_NO_DOCUMENT: 'TEACHER_NO_DOCUMENT',
  TEACHER_NO_DIGITAL_MANUAL: 'TEACHER_NO_DIGITAL_MANUAL',
} as const;

/**
 * @deprecated License and access checks are being centralized in `core/access/module-access`.
 * Use `useModuleAccess` for new code and migrate existing call sites when touching them.
 * `hasGroupFeatures` is account-level and will move to an account access check in `core/access`.
 */
function useLicenses() {
  const {
    identity: { isTeacherAlike },
  } = useContext(IdentityContext);
  const { account, isAccountLoaded } = useContext(AccountContext);
  const studioGroup = useStudioGroup();

  function hasOnlyDemoProductLicenseForTeacher(module: Module | undefined) {
    if (!module) return false;
    return getModuleLicense(module, isTeacherAlike) === MODULE_LICENSE.DEMO;
  }

  function getUserLicenseValidationType(module: Module): keyof typeof LICENSE_VALIDATION_TYPES {
    const license = getModuleLicense(module, isTeacherAlike);
    return isLicensed(license) ? LICENSE_VALIDATION_TYPES.LICENSED : license;
  }

  function isModuleLicensed(module?: Module | null): boolean {
    if (!module) return false;
    return isLicensed(getModuleLicense(module, isTeacherAlike));
  }

  function hasGroupFeatures(): boolean {
    if (!isAccountLoaded) return true;
    return studioGroup !== 'BAO' || account.hasBaollLicense;
  }

  return {
    hasOnlyDemoProductLicenseForTeacher,
    getUserLicenseValidationType,
    isModuleLicensed,
    hasGroupFeatures,
  };
}

export default useLicenses;
